import React from 'react';
import PropTypes from 'prop-types';

import { useTranslation } from 'react-i18next';
import { Modal, Button, Form, SelectLabel, Tooltip, CheckboxLabel, Counter, TranslateDropdown, Message, ToastContext } from '@weezevent/nacre';
import Styles from '../../styles/rate/groupModal.module.css';

import useAppContext from '../../context/useAppContext';
import { requiredField, useForm } from '../../utils/validators';
import { LANGUAGES } from '../../components/translations/translationModal';

export default function GroupModal({ event, showModal, onClose, onAccept, groupId, GroupClass, groups = [], quotaTakenByGroup, group, rates, settings }) {
    const { t } = useTranslation();
    const toast = React.useContext(ToastContext);
    const [{ organization }] = useAppContext();

    const [loading, setLoading] = React.useState(false);
    const [isSubmitting, setIsSubmitting] = React.useState(false);

    const languageOptions = React.useMemo(() => {
        let list = event ? event : organization;
        return list.languages.map(lang => {
            return {
                label: LANGUAGES[lang.toLowerCase()],
                value: lang
            };
        });
    }, [organization, event]);

    const defaultLanguage = React.useMemo(() => {
        let defaultLocale = event ? event.default_language : languageOptions.find(it => it.value.includes(organization.default_locale.replace('_', '-').toLowerCase()));
        return event ? defaultLocale : defaultLocale?.value || languageOptions[0]?.value;
    }, [organization, languageOptions, event]);

    const sumQuotaChildren = React.useMemo(() => {
        const sumQuotasSubGroups = groups
            .filter(group => group.parent === groupId)
            .reduce((acc, _g) => {
                // Calculate the maximum value between:
                // 1. The sum of the rates quantities for this subgroup
                // 2. The quota defined for this subgroup
                let sum = Math.max(
                    rates?.reduce((acc, rate) => (_g.id === rate.group ? acc + rate.quantity : acc), 0),
                    _g.quota
                );
                return acc + sum;
            }, 0);

        // Sum the rates of the current group
        const allDirectRatesQuantity = rates?.reduce((acc, rate) => (rate.group === groupId ? acc + rate.quantity : acc), 0);

        return allDirectRatesQuantity + sumQuotasSubGroups;
    }, [groups, groupId, rates]);

    const minQuota = React.useMemo(() => {
        return quotaTakenByGroup?.[groupId] || 0;
    }, [quotaTakenByGroup, groupId]);

    const defaultForm = {
        name: '',
        parent: null,
        quota: null,
        display_order: null,
        translations: {
            name: {}
        }
    };

    let [formState, onChangeForm, onChangeFormObject, formErrors, formValidate] = useForm(defaultForm, {
        name: requiredField
    });

    const displayWarningMessage = React.useMemo(() => {
        // Handle warning message for RateTemplate
        if (!event && group?.parent) {
            const parentGroup = groups.find(g => g.uuid === group.parent);
            if (formState.quota > parentGroup?.quota) {
                return 'max';
            }
        }

        if (formState.quota < minQuota) {
            return null;
        }

        // Check if form's quota is less than the sum of children's quotas
        if (formState.quota !== null && formState.quota < sumQuotaChildren) {
            return 'min';
        }

        // Handle case if the group has a parent
        if (group?.parent) {
            const parentGroup = groups.find(g => g.id === group.parent);
            if (formState.quota > parentGroup?.quota || (settings?.is_limited_quota && formState.quota > settings.quota)) {
                return 'max';
            }
        } else {
            // Handle case if the group has no parent
            if (settings?.is_limited_quota && formState.quota > settings.quota) {
                return 'max';
            }
        }

        return null;
    }, [event, minQuota, formState, sumQuotaChildren, group, groups, settings]);

    React.useEffect(() => {
        if (showModal && groupId) {
            setLoading(true);
            GroupClass.get({
                id: groupId,
                organizationId: organization.pk,
                eventId: event ? event.id : null
            })
                .then(groupElement => {
                    let form = {
                        name: groupElement.name,
                        parent: groupElement.parent,
                        quota: groupElement.quota,
                        display_order: groupElement.display_order,
                        translations: groupElement.translations
                    };

                    if ('name' in groupElement.translations) {
                        form.translations.name[defaultLanguage] = groupElement.name;
                    } else {
                        form.translations['name'] = { [defaultLanguage]: groupElement.name };
                    }

                    onChangeFormObject(form);
                })
                .catch(e => {
                    console.log(e);
                    toast.error(t('ticket.rates.loadGroupError'));
                    onClose();
                })
                .finally(() => {
                    setLoading(false);
                });
        }
    }, [onChangeFormObject, GroupClass, showModal]);

    const closeAndResetForm = React.useCallback(() => {
        onClose();
        onChangeFormObject(defaultForm);
        formErrors['name'] = null;
    }, [onChangeFormObject, onClose]);

    const hasNameTranslations = React.useMemo(() => {
        if (!('name' in formState.translations) || !Object.keys(formState.translations.name)) {
            return false;
        }
        return defaultLanguage in formState.translations.name && Object.keys(formState.translations.name).length > 1;
    }, [formState, defaultLanguage]);

    const getDisplayOrder = React.useCallback(() => {
        // display_order must be set and updated to match the display order of existing parent
        let displayOrder = formState.display_order ?? 0;

        if (!formState.parent && Boolean(!group || group?.parent)) {
            let groupOrderArray = groups.map(g => g.display_order);
            return groupOrderArray.length ? Math.max(...groupOrderArray) + 1 : displayOrder;
        }

        if ((formState.parent && !group?.parent) || formState.parent !== group?.parent) {
            let parent = groups.find(g => g.pk === formState.parent);
            return parent.display_order + 1;
        }

        return displayOrder;
    }, [formState, group, groups]);

    const handleSubmit = React.useCallback(() => {
        if (!formValidate()) {
            return toast.error(t('ticket.common.errorFields'));
        }

        let groupTmp = {
            name: formState.name,
            parent: formState.parent,
            quota: formState.quota,
            display_order: getDisplayOrder()
        };

        if (hasNameTranslations) {
            groupTmp.translations = formState.translations;
        }

        if (groupId) {
            groupTmp.id = groupId;
            groupTmp.uuid = groupId;
        }

        let groupObject = new GroupClass(groupTmp);
        setIsSubmitting(true);
        groupObject
            .save({
                organizationId: organization.pk,
                eventId: event ? event.id : null
            })
            .then(() => {
                toast.success(t(`ticket.rates.${groupId ? 'editGroupSuccess' : 'creationGroupSuccess'}`));
                onAccept();
            })
            .catch(() => {
                toast.error(t(`ticket.rates.${groupId ? 'editGroupError' : 'creationGroupError'}`));
            })
            .finally(() => {
                setIsSubmitting(false);
            });
    }, [formState, formValidate, groupId, GroupClass, organization, hasNameTranslations, getDisplayOrder]);

    const mapToGroups = React.useCallback(group => ({ key: group.pk, text: group.name ?? group?.group_template?.name, value: group.pk }), []);
    const optionsGroups = React.useMemo(() => {
        //Preselect no groups
        let firstOption = [
            {
                key: 'no_group',
                text: t('ticket.rates.groupModal.noParentGroup'),
                value: 'no_group'
            }
        ];
        //exclude current group with filter()
        return firstOption.concat(groups.filter(group => groupId !== group.pk && !group.parent).map(mapToGroups));
    }, [groups, mapToGroups]);

    const onChangeUnlimitedQuantity = React.useCallback(
        (e, { value, checked }) => {
            if (!checked) {
                onChangeForm('quota', 0);
            } else {
                onChangeForm('quota', null);
            }
        },
        [onChangeForm]
    );

    const isDisabledButton = React.useMemo(() => {
        if (isSubmitting || (formState.quota !== null && formState.quota < minQuota)) {
            return true;
        }
        return false;
    }, [isSubmitting, formState, minQuota]);

    if (loading || !showModal) {
        return null;
    }

    return (
        <Modal
            className={Styles['group-modal']}
            open={showModal}
            onClose={closeAndResetForm}
            title={t(groupId ? 'ticket.common.action.edit' : 'ticket.rates.addGroup')}
            subtitle={t(`ticket.rates.groupModal.${groupId ? 'subtitleEdit' : 'subtitleCreate'}`)}
            submitButton={<Button primary disabled={isDisabledButton} onClick={handleSubmit} label={t(`ticket.common.action.save`)} />}
            cancelButton={<Button disabled={isSubmitting} onClick={closeAndResetForm} label={t(`ticket.common.action.cancel`)} />}
        >
            <Form>
                <Form.Field>
                    <GroupNameInput
                        errors={formErrors['name'] ? true : false}
                        formState={formState}
                        onChangeFormObject={onChangeFormObject}
                        defaultLanguage={defaultLanguage}
                        languageOptions={languageOptions}
                    />
                    <Form.FieldErrors errors={formErrors['name'] ? [t(formErrors['name'])] : null} />
                </Form.Field>
                {groups.filter(group => groupId !== group.pk).length > 0 && (
                    <Form.Field>
                        <Form.Label className={Styles['remove-margin-bottom']} optionalText={t('ticket.common.labelOptional')}>
                            <span className={Styles['group-modal-label-parent-group']}>
                                {t(`ticket.rates.groupModal.parentGroup`)}
                                <Tooltip position={'bottom'} title={t('ticket.rates.groupModal.parentGroupTooltip')} triggerOnHover={true} visible={true}>
                                    <span className={Styles['group-modal-help-icon']} />
                                </Tooltip>
                            </span>
                        </Form.Label>

                        <SelectLabel
                            value={formState.parent === null ? 'no_group' : formState.parent}
                            onChange={value => onChangeForm('parent', value === 'no_group' ? null : value)}
                            options={optionsGroups}
                            placeholder={t('ticket.rates.groupModal.parentGroupPlaceholder')}
                        />
                    </Form.Field>
                )}
                <Form.Field className={Styles['remove-margin-bottom']}>
                    <Form.Label className={Styles['remove-margin-bottom']} optionalText={t('ticket.common.labelOptional')}>
                        {t(`ticket.rates.groupModal.groupQuota`)}
                    </Form.Label>
                    <Form.Label className={Styles['group-modal-form-label-subtitle']}>{t(`ticket.rates.groupModal.groupQuotaSubtitle`)}</Form.Label>
                    {event?.use_sessions ? (
                        <Message type="default" title={t('ticket.rates.infos.session.title')}>
                            {t('ticket.rates.infos.session.message')}
                        </Message>
                    ) : (
                        <>
                            <div className={Styles['group-modal-quota']}>
                                <Counter
                                    style={{ maxWidth: '150px', marginRight: '15px' }}
                                    placeholder={0}
                                    onChange={(event, value) => onChangeForm('quota', value)}
                                    disabled={formState.quota === null}
                                    value={formState.quota}
                                    min={0}
                                />
                                <CheckboxLabel
                                    onChange={onChangeUnlimitedQuantity}
                                    checked={formState.quota === null}
                                    label={t(`ticket.rates.groupModal.unlimitedGroupQuota`)}
                                    name={'unlimited_quota'}
                                />
                            </div>
                            <div>
                                <span>{t('ticket.rates.groupModal.quotaQuantity', { min: minQuota, max: t('ticket.rates.groupModal.noLimit') })}</span>
                                {formState.quota !== null && formState.quota < minQuota && (
                                    <div className={Styles['quota-exceeded-warning']}>
                                        <Message type="danger">{t('ticket.rates.groupModal.quotaExceededDanger')}</Message>
                                    </div>
                                )}
                                {displayWarningMessage && (
                                    <div className={Styles['quota-exceeded-warning']}>
                                        <Message type="warning">{t(`ticket.rates.groupModal.quotaExceeded-${displayWarningMessage}`)}</Message>
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </Form.Field>
            </Form>
        </Modal>
    );
}

GroupModal.defaultProps = {
    event: null,
    groupId: null,
    display_order: null,
    showModal: null,
    groups: [],
    quotaTakenByGroup: {},
    group: null,
    rates: [],
    settings: {}
};

GroupModal.propTypes = {
    event: PropTypes.object,
    onAccept: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
    showModal: PropTypes.bool,
    groupId: PropTypes.number,
    display_order: PropTypes.number,
    GroupClass: PropTypes.func.isRequired,
    groups: PropTypes.array,
    quotaTakenByGroup: PropTypes.object,
    group: PropTypes.object,
    rates: PropTypes.array,
    settings: PropTypes.object
};

function GroupNameInput({ formState, onChangeFormObject, defaultLanguage, languageOptions, errors = false }) {
    const { t } = useTranslation();
    const [selectedNameLanguage, setSelectedNameLanguage] = React.useState(defaultLanguage);

    const groupTranslations = React.useMemo(() => {
        if (!('name' in formState.translations)) {
            return [];
        }
        return Object.keys(formState.translations.name);
    }, [formState]);

    const groupNameValue = React.useMemo(() => {
        return !('name' in formState.translations) || selectedNameLanguage === defaultLanguage
            ? formState.name || formState?.group_template?.name
            : formState.translations.name[selectedNameLanguage];
    }, [defaultLanguage, formState, selectedNameLanguage]);

    const maxLength = 60;
    const [valueLength, setValueLength] = React.useState(groupNameValue?.length || 0);

    const handleLanguage = React.useCallback(
        value => {
            setSelectedNameLanguage(value);
            let _form = { ...formState };
            let translations = _form.translations.name;

            // Set default value upon language selection
            if (!(value in translations)) {
                translations[value] = '';
            }

            // Delete empty keys
            Object.keys(translations)
                .filter(k => k !== value)
                .forEach(key => {
                    if (translations[key] === '' && translations[key] !== null) {
                        delete translations[key];
                    }
                });
            _form.translations.name = translations;
            onChangeFormObject({ ..._form }, true);
        },
        [formState, onChangeFormObject]
    );

    const cleanTranslations = React.useCallback(
        trans => {
            let transToApply = { ...trans };
            if (
                Object.keys(trans).length > 1 &&
                Object.entries(trans)
                    .filter(k => k[0] !== defaultLanguage)
                    .every(key => key[1] === null)
            ) {
                transToApply[defaultLanguage] = null;
            }

            return transToApply;
        },
        [defaultLanguage]
    );

    const handleTranslations = React.useCallback(
        value => {
            let _form = { ...formState };
            let trans = formState.translations.name;
            trans[selectedNameLanguage] = value;
            if (selectedNameLanguage !== defaultLanguage) {
                if (!value) {
                    trans[selectedNameLanguage] = null;
                }
            } else {
                _form.name = value;
            }
            _form.translations.name = cleanTranslations(trans);
            onChangeFormObject({ ..._form }, true);
        },
        [onChangeFormObject, formState, defaultLanguage, cleanTranslations, selectedNameLanguage]
    );

    const handleChange = React.useCallback(
        ({ target: { value } }) => {
            handleTranslations(value);
            setValueLength(value.length);
        },
        [handleTranslations]
    );

    return (
        <>
            <Form.Label className={Styles['remove-margin-bottom']}>
                <div className={Styles['group-label-input']}>
                    {t(`ticket.rates.groupName`)}
                    <TranslateDropdown defaultValue={defaultLanguage} languages={languageOptions} onChange={value => handleLanguage(value)} selected={groupTranslations} />
                </div>
            </Form.Label>

            <Form.Input errors={errors} value={groupNameValue} onChange={handleChange} maxLength={200} />
            <span className={Styles['input-length']}>
                <span>
                    {valueLength || 0}
                    {`/${maxLength}`}
                </span>
                {valueLength > 60 && <span className={Styles['input-length-message']}>{t('ticket.rates.infos.group-name-too-long')}</span>}
            </span>
        </>
    );
}
